import { useCallback, useId, useState } from 'react';
import { Card } from '@amzn/stencil-react-components/card';
import { Col, Row, View } from '@amzn/stencil-react-components/layout';
import { H2, H3, Text } from '@amzn/stencil-react-components/text';
import { token } from '@amzn/stencil-design-tokens/js/web/utils';
import { Button, ButtonVariant, ButtonSize } from '@amzn/stencil-react-components/button';
import {
  MessageBanner,
  MessageBannerType,
} from '@amzn/stencil-react-components/message-banner';
// standards-ignore: cross-feature — IconPlusSmall is a generic "add" affordance; a shared wrapper over a bare icon would add indirection without value
import { IconPlusSmall } from '@amzn/stencil-react-icons';

import { InfoTooltip } from '../../../shared/ui/forms/InfoTooltip';
import { VerticalDivider } from '../../../shared/ui/VerticalDivider';
import { ReviewQuestionToggleField } from './ReviewQuestionToggleField';
import { useMessages } from '../../../shared/i18n/useMessages';
import { SubQuestionRow } from './SubQuestionRow';
import { SUB_QUESTIONS_BUNDLE, type SubKey } from './subQuestionsMessages';
import {
  resolveConditionalToggle,
  canEnableConditional,
  type ConditionalToggleMessage,
} from '../../../shared/domain/subQuestionResponseType';
import { validateSubQuestions } from '../../../shared/domain/subQuestionValidation';
import {
  createEmptySubQuestion,
  nextOrderIndex,
  rowHasIssue,
} from '../logic/subQuestionsStepLogic';
import type { ReviewQuestion, QuestionContent, SubQuestion } from '../logic/reviewTypes';

const EMPTY_SUB_QUESTIONS: readonly SubQuestion[] = [];

/** Repeated layout values, hoisted so the same string doesn't appear inline
 *  across multiple Rows (matches the SubQuestionRow convention). */
const SPACE_BETWEEN = 'space-between';
const WRAP = 'wrap';
/** Shared inline gap so identical `alignItems + gridGap` Row openings don't
 *  repeat as a literal (the standards scanner flags repeated JSX fragments). */
const GAP_200 = 'dimensions.spacing.200';

/** Bordered, rounded frame for the conditional-display setting panel so it
 *  reads as a distinct control governing the list below (not a list item).
 *  Module-level for emotion reference stability. */
const CONDITIONAL_PANEL_STYLE = {
  border: `${token('dimensions.border.width.100')} solid ${token('color.border.primary')}`,
  borderRadius: token('dimensions.border.radius.100'),
} as const;

/** The informational conditional-toggle outcomes — everything except
 *  'blocked'. The blocked state is NOT stored: it's derived live from
 *  {@link canEnableConditional} so it self-clears the instant the author fixes
 *  the offending sub (a stored 'blocked' snapshot would outlive the fix). */
type ConditionalInfoMessage = Exclude<ConditionalToggleMessage, 'blocked'>;

/** Maps an informational {@link ConditionalInfoMessage} to its (always
 *  informational) banner i18n key. A switch (not a keyed object) avoids a
 *  dynamic object-index lint flag and stays exhaustive over the union. */
function conditionalInfoMessageKey(message: ConditionalInfoMessage): SubKey {
  switch (message) {
    case 'defaulted':
      return 'subConditionalDefaultedInfo';
    case 'enabled':
      return 'subConditionalEnabledInfo';
    case 'disabled':
      return 'subConditionalDisabledInfo';
  }
}

export interface SubQuestionsStepProps {
  /** The question under review whose sub-questions this step edits. */
  readonly question: ReviewQuestion;
  /** Emits a new question with the edited sub-questions. Parent owns state. */
  readonly onQuestionChange: (next: ReviewQuestion) => void;
  readonly disabled?: boolean;
  readonly dataTestId?: string;
}

/**
 * Second review wizard step: the editable sub-question list. Each sub-question
 * is a collapsible `SubQuestionRow` (title, rich-text instruction, the four
 * Pass/Fail requirement toggles, and an optional task). Sub-questions can be
 * added and deleted.
 *
 * Each row also picks its ERT response type; this step owns the parent
 * conditional-display toggle (gated so it can only be enabled when every sub
 * is a data-collection type). Fully controlled — edits `content.subQuestions`
 * and `configuration.hasConditionalSubQuestions`, emitting a new
 * `ReviewQuestion`; the owning page/hook holds the draft state.
 */
export function SubQuestionsStep({
  question,
  onQuestionChange,
  disabled = false,
  dataTestId,
}: SubQuestionsStepProps): JSX.Element {
  const { t } = useMessages<SubKey>(SUB_QUESTIONS_BUNDLE);
  const subQuestions = question.content.subQuestions ?? EMPTY_SUB_QUESTIONS;
  const conditionalEnabled = question.configuration.hasConditionalSubQuestions ?? false;
  // orderIndex of the row just added via the Add button, so only that row
  // starts expanded. Existing rows stay collapsed (matches the mock).
  const [addedOrderIndex, setAddedOrderIndex] = useState<number | null>(null);
  // INFORMATIONAL message surfaced after an ALLOWED conditional-toggle change
  // (defaulted / enabled / disabled — matches the manual flow's Alert).
  // Dismissible and re-evaluated on every allowed flip. The 'blocked' case is
  // deliberately NOT stored here — see `conditionalBlocked` below.
  const [conditionalMessage, setConditionalMessage] =
    useState<ConditionalInfoMessage | null>(null);
  // Whether the author has attempted to enable conditional display while it
  // was blocked. Gates the LIVE blocked banner so an untouched draft with a
  // Pass/Fail sub isn't pre-emptively flagged. Reset the moment the block is
  // resolved (any edit that makes the set enableable — see emitSubQuestions),
  // on a successful enable, or on dismiss — so a stale attempt can't re-trigger
  // the banner on a later unrelated edit.
  const [blockedAttempt, setBlockedAttempt] = useState(false);
  // Bumped on every REJECTED toggle attempt. Used as the switch's `key` so
  // React remounts it back to the (unchanged) `checked` value: Stencil's
  // ToggleSwitch optimistically flips its thumb on the native click, and React
  // won't reset a controlled input when the `checked` prop value is unchanged
  // (false → false). Remounting forces the visual state back to Off.
  const [blockedNonce, setBlockedNonce] = useState(0);
  // Wires the switch (external-label mode) to the panel's bold heading text.
  const conditionalHeadingId = useId();
  // LIVE blocked state: the author tried to enable conditional display AND it's
  // still not enableable (a Pass/Fail sub still exists). Derived from the
  // current sub list every render — NOT a stored 'blocked' snapshot — so
  // converting the last Pass/Fail sub to a data-collection type clears both the
  // error banner and the row badges immediately, without another toggle click.
  const conditionalBlocked = blockedAttempt && !canEnableConditional(subQuestions);
  // While blocked, flag the Pass/Fail rows (the ones the author must change)
  // with a warning badge so they're scannable in a long list — the blocked
  // message alone doesn't say WHICH rows.
  const highlightPassFail = conditionalBlocked;
  // Persistent per-row validity (e.g. a select sub with <2 options). Always
  // computed so the row's "Incomplete" indicator is visible regardless of how
  // the user navigated here — the same rule the publish gate enforces.
  const violations = validateSubQuestions(subQuestions);

  const emitSubQuestions = useCallback(
    (next: readonly SubQuestion[]) => {
      // If this edit makes the set enableable again, retire the prior blocked
      // attempt so it can't re-trigger the banner on a LATER unrelated edit
      // (e.g. adding a new Pass/Fail sub). Effect-free: reset at the event that
      // resolves the block, not via a useEffect watching the list. No-op when
      // already false (React bails on an unchanged value).
      if (canEnableConditional(next)) setBlockedAttempt(false);
      const nextContent: QuestionContent = {
        ...question.content,
        subQuestions: [...next],
      };
      onQuestionChange({ ...question, content: nextContent });
    },
    [question, onQuestionChange],
  );

  // Flip the parent conditional-display toggle. The pure resolver encodes the
  // v1 rule (block if any Pass/Fail sub, default triggers to BOTH on enable,
  // strip on disable) and picks the message. We ALWAYS set the message, and
  // ONLY persist the new flag + subs when the change is allowed — a blocked
  // enable leaves state untouched and shows the error.
  const handleConditionalToggle = useCallback(
    (nextEnabled: boolean) => {
      const outcome = resolveConditionalToggle(subQuestions, nextEnabled);
      if (!outcome.allowed) {
        // Rejected (blocked): record the attempt so the LIVE blocked banner +
        // badges show (they self-clear once the offending sub is fixed). No
        // stored 'blocked' message — that snapshot would outlive the fix.
        setBlockedAttempt(true);
        // Clear any stale informational banner (e.g. a prior 'disabled') so it
        // can't co-render beneath the blocked error — the allowed branch and
        // dismiss both clear it, so this branch must too (symmetry).
        setConditionalMessage(null);
        // The switch already flipped its thumb on the native click, and React
        // won't reset it because `checked` is unchanged (false → false). Bump
        // the nonce so its `key` changes and it remounts to Off.
        setBlockedNonce((n) => n + 1);
        return;
      }
      // Allowed: any prior blocked attempt is resolved, and the outcome message
      // is necessarily informational (never 'blocked' when allowed).
      setBlockedAttempt(false);
      setConditionalMessage(outcome.message as ConditionalInfoMessage | null);
      const nextContent: QuestionContent = {
        ...question.content,
        subQuestions: [...outcome.subs],
      };
      onQuestionChange({
        ...question,
        content: nextContent,
        configuration: {
          ...question.configuration,
          hasConditionalSubQuestions: outcome.enabled,
        },
      });
    },
    [subQuestions, question, onQuestionChange],
  );

  // Dismiss BOTH conditional surfaces: the informational banner and the live
  // blocked banner (clearing the attempt flag stops the derived block).
  const dismissConditionalMessage = useCallback(() => {
    setConditionalMessage(null);
    setBlockedAttempt(false);
  }, []);

  const handleAdd = useCallback(() => {
    const order = nextOrderIndex(subQuestions);
    setAddedOrderIndex(order);
    emitSubQuestions([
      ...subQuestions,
      createEmptySubQuestion(order, conditionalEnabled),
    ]);
  }, [subQuestions, emitSubQuestions, conditionalEnabled]);

  const handleChangeAt = useCallback(
    (index: number, nextSub: SubQuestion) => {
      emitSubQuestions(subQuestions.map((sq, i) => (i === index ? nextSub : sq)));
    },
    [subQuestions, emitSubQuestions],
  );

  const handleDeleteAt = useCallback(
    (index: number) => {
      emitSubQuestions(subQuestions.filter((_, i) => i !== index));
    },
    [subQuestions, emitSubQuestions],
  );

  return (
    // No outer card: the review page wraps the whole step in a single outer
    // card. This step contributes its H2 heading plus the inner "Sub question
    // & Sub Task" section card — matching the mock.
    <Col
      gridGap="dimensions.spacing.400"
      width="100%"
      {...(dataTestId !== undefined ? { dataTestId } : {})}
    >
      <H2>{t('subStepHeading')}</H2>

      <Card
        padding="dimensions.spacing.300"
        {...(dataTestId !== undefined ? { dataTestId: `${dataTestId}-section` } : {})}
      >
        <Col gridGap="dimensions.spacing.300" width="100%">
          <Row alignItems="center" justifyContent={SPACE_BETWEEN} flexWrap={WRAP}>
            <Row alignItems="center" gridGap={GAP_200}>
              <H3>{t('subSectionHeading')}</H3>
              <VerticalDivider />
              <InfoTooltip text={t('subSectionInfo')} ariaLabel={t('subInfoAltText')} />
            </Row>
            <Button
              variant={ButtonVariant.Secondary}
              size={ButtonSize.Small}
              icon={<IconPlusSmall aria-hidden="true" />}
              onClick={handleAdd}
              disabled={disabled}
              data-testid={dataTestId ? `${dataTestId}-add` : undefined}
            >
              {t('subAddButton')}
            </Button>
          </Row>

          {/* Conditional-display setting — a framed panel that governs the
           *  whole list below (each sub shows only for the parent Pass/Fail
           *  answer chosen per row). Contained with a bold title + description
           *  so it reads as a setting, not a list item. Can only be enabled
           *  when every sub is a data-collection type. */}
          <Col gridGap="dimensions.spacing.200">
            <View
              padding="dimensions.spacing.300"
              backgroundColor="color.surface.bg-muted"
              css={CONDITIONAL_PANEL_STYLE}
            >
              <Row
                alignItems="center"
                justifyContent={SPACE_BETWEEN}
                gridGap="dimensions.spacing.300"
                flexWrap={WRAP}
              >
                <Col gridGap="dimensions.spacing.50" flex={1} minWidth={0}>
                  <Text id={conditionalHeadingId} fontWeight="bold">
                    {t('subConditionalToggleLabel')}
                  </Text>
                  <Text color="color.text.secondary" fontSize="T100">
                    {conditionalEnabled
                      ? t('subConditionalOnDescription')
                      : t('subConditionalOffDescription')}
                  </Text>
                </Col>
                <ReviewQuestionToggleField
                  key={`conditional-toggle-${blockedNonce}`}
                  labelId={conditionalHeadingId}
                  checked={conditionalEnabled}
                  onChange={handleConditionalToggle}
                  disabled={disabled}
                  trackingId="audit-whiz.review.sub-q.conditional-display"
                  {...(dataTestId !== undefined
                    ? { dataTestId: `${dataTestId}-conditional-toggle` }
                    : {})}
                />
              </Row>
            </View>

            {conditionalBlocked ? (
              <MessageBanner
                type={MessageBannerType.Error}
                isDismissible
                onDismissed={dismissConditionalMessage}
                dismissButtonAltText={t('subConditionalDismissAltText')}
                {...(dataTestId !== undefined
                  ? { dataTestId: `${dataTestId}-conditional-blocked` }
                  : {})}
              >
                {t('subConditionalBlockedError')}
              </MessageBanner>
            ) : null}

            {conditionalMessage !== null ? (
              <MessageBanner
                type={MessageBannerType.Informational}
                isDismissible
                onDismissed={dismissConditionalMessage}
                dismissButtonAltText={t('subConditionalDismissAltText')}
                {...(dataTestId !== undefined
                  ? { dataTestId: `${dataTestId}-conditional-message` }
                  : {})}
              >
                {t(conditionalInfoMessageKey(conditionalMessage))}
              </MessageBanner>
            ) : null}
          </Col>

          {subQuestions.length === 0 ? (
            <Text color="color.text.secondary">{t('subEmptyText')}</Text>
          ) : (
            subQuestions.map((sub, index) => (
              <SubQuestionRow
                key={sub.subQuestionId ?? `order-${sub.orderIndex}`}
                sub={sub}
                index={index}
                disabled={disabled}
                defaultExpanded={sub.orderIndex === addedOrderIndex}
                conditionalEnabled={conditionalEnabled}
                hasIssue={rowHasIssue(sub, index, highlightPassFail, violations)}
                onChange={handleChangeAt}
                onDelete={handleDeleteAt}
                {...(dataTestId !== undefined
                  ? { dataTestId: `${dataTestId}-row-${index}` }
                  : {})}
              />
            ))
          )}
        </Col>
      </Card>
    </Col>
  );
}
